import React, { useState, useCallback, MouseEvent } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

export type NodeType = 'trigger' | 'action' | 'condition' | 'loop';

export interface AutomationNodeData {
  label: string;
  type: NodeType;
  config?: Record<string, any>;
}

interface FlowchartEditorProps {
  initialNodes?: Node<AutomationNodeData>[];
  initialEdges?: Edge[];
  onSave?: (nodes: Node[], edges: Edge[]) => void;
  onClose?: () => void;
}

// Node components with glassmorphism style
const TriggerNode: React.FC<{ data: AutomationNodeData }> = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="target" position={Position.Top} style={{ width: 12, height: 12, background: '#22c55e', border: '2px solid #16a34a' }} />
    <div style={{
      padding: 16,
      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
      border: '1px solid rgba(255,255,255,0.2)',
      minWidth: 180
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22
        }}>⚡</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{data.label}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Gatilho</div>
        </div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ width: 12, height: 12, background: '#22c55e', border: '2px solid #16a34a' }} />
  </div>
);

const ActionNode: React.FC<{ data: AutomationNodeData }> = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="target" position={Position.Top} style={{ width: 12, height: 12, background: '#3b82f6', border: '2px solid #2563eb' }} />
    <div style={{
      padding: 16,
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
      border: '1px solid rgba(255,255,255,0.2)',
      minWidth: 180
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22
        }}>🎯</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{data.label}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Ação</div>
        </div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ width: 12, height: 12, background: '#3b82f6', border: '2px solid #2563eb' }} />
  </div>
);

const ConditionNode: React.FC<{ data: AutomationNodeData }> = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="target" position={Position.Top} style={{ width: 12, height: 12, background: '#f97316', border: '2px solid #ea580c' }} />
    <div style={{
      padding: 16,
      background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.9) 0%, rgba(234, 88, 12, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(249, 115, 22, 0.3)',
      border: '1px solid rgba(255,255,255,0.2)',
      minWidth: 180
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22
        }}>🔀</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{data.label}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Condição</div>
        </div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ width: 12, height: 12, background: '#f97316', border: '2px solid #ea580c' }} />
  </div>
);

const LoopNode: React.FC<{ data: AutomationNodeData }> = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="target" position={Position.Top} style={{ width: 12, height: 12, background: '#a855f7', border: '2px solid #9333ea' }} />
    <div style={{
      padding: 16,
      background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.9) 0%, rgba(147, 51, 234, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(168, 85, 247, 0.3)',
      border: '1px solid rgba(255,255,255,0.2)',
      minWidth: 180
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22
        }}>🔄</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{data.label}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Loop</div>
        </div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ width: 12, height: 12, background: '#a855f7', border: '2px solid #9333ea' }} />
  </div>
);

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  loop: LoopNode,
};

const defaultNodes: Node<AutomationNodeData>[] = [
  {
    id: '1',
    type: 'trigger',
    position: { x: 400, y: 50 },
    data: { label: 'Lead Criado', type: 'trigger', config: { trigger_type: 'lead_created' } },
  },
  {
    id: '2',
    type: 'action',
    position: { x: 250, y: 200 },
    data: { label: 'Enviar WhatsApp', type: 'action', config: { action_type: 'send_message' } },
  },
  {
    id: '3',
    type: 'condition',
    position: { x: 550, y: 200 },
    data: { label: 'Valor > R$1000?', type: 'condition', config: { operator: '>' } },
  },
];

const defaultEdges: Edge[] = [
  { id: 'e1-2', source: '1', target: '2', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#64748b', strokeWidth: 2 } },
  { id: 'e1-3', source: '1', target: '3', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#64748b', strokeWidth: 2 } },
];

// Sidebar component button
interface ComponentBtnProps {
  icon: string;
  title: string;
  subtitle: string;
  gradientFrom: string;
  gradientTo: string;
  onClick: () => void;
}

const ComponentBtn: React.FC<ComponentBtnProps> = ({ icon, title, subtitle, gradientFrom, gradientTo, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      background: 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: 12,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.2s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = `linear-gradient(135deg, ${gradientFrom}20, ${gradientTo}20)`;
      e.currentTarget.style.borderColor = gradientFrom;
      e.currentTarget.style.transform = 'translateX(4px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
      e.currentTarget.style.transform = 'translateX(0)';
    }}
  >
    <div style={{
      width: 32,
      height: 32,
      borderRadius: 8,
      background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 16
    }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>{title}</div>
      <div style={{ fontSize: 10, color: '#6b7280' }}>{subtitle}</div>
    </div>
  </button>
);

export default function FlowchartEditor({ onSave, onClose }: FlowchartEditorProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(defaultEdges);
  const [selectedNode, setSelectedNode] = useState<Node<AutomationNodeData> | null>(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({
      ...params,
      type: 'smoothstep',
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { stroke: '#64748b', strokeWidth: 2 }
    }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((_event: MouseEvent, node: Node) => {
    setSelectedNode(node as Node<AutomationNodeData>);
    setShowNodeModal(true);
  }, []);

  const addNode = useCallback((type: NodeType, label: string, config: Record<string, any> = {}) => {
    const newNode: Node<AutomationNodeData> = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: 400 + Math.random() * 100 - 50, y: 150 + Math.random() * 100 },
      data: { label, type, config }
    };
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(nodes, edges);
    }
  }, [nodes, edges, onSave]);

  const handleClear = useCallback(() => {
    if (window.confirm('Limpar todo o fluxo?')) {
      setNodes([]);
      setEdges([]);
    }
  }, [setNodes, setEdges]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        height: 64,
        background: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        flexShrink: 0,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20
          }}>⚡</div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: 0 }}>Editor de Fluxos</h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Arraste e conecte para criar automações</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleClear}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: '1px solid rgba(0,0,0,0.1)',
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              color: '#374151',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s'
            }}
          >
            🗑️ Limpar
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: 'none',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
              transition: 'all 0.2s'
            }}
          >
            💾 Salvar
          </button>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.2s'
              }}
            >
              ✕ Fechar
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        {/* Flow Canvas */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            style={{ width: '100%', height: '100%' }}
          >
            <Background color="#94a3b8" gap={24} size={1} />
            <Controls
              style={{
                bottom: 24,
                left: 24,
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            />
            <MiniMap
              style={{
                bottom: 24,
                right: sidebarOpen ? 284 : 24,
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
              maskColor="rgba(99, 102, 241, 0.1)"
            />
          </ReactFlow>
        </div>

        {/* Sidebar Toggle Button (when collapsed) */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 10,
              width: 44,
              height: 44,
              borderRadius: 12,
              border: 'none',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ◀
          </button>
        )}

        {/* Sidebar - Right Side */}
        {sidebarOpen && (
          <div style={{
            width: 260,
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255,255,255,0.3)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            boxShadow: '-4px 0 20px rgba(0,0,0,0.05)'
          }}>
            {/* Sidebar Header */}
            <div style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>🧩 Componentes</span>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  border: 'none',
                  background: 'rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  color: '#6b7280'
                }}
              >▶</button>
            </div>

            {/* Components List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
              {/* Triggers */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#22c55e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ⚡ Gatilhos
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <ComponentBtn icon="📋" title="Lead Criado" subtitle="Novo lead" gradientFrom="#22c55e" gradientTo="#16a34a"
                    onClick={() => addNode('trigger', 'Lead Criado', { trigger_type: 'lead_created' })} />
                  <ComponentBtn icon="🔄" title="Status Mudou" subtitle="Alteração" gradientFrom="#22c55e" gradientTo="#16a34a"
                    onClick={() => addNode('trigger', 'Status Mudou', { trigger_type: 'status_changed' })} />
                  <ComponentBtn icon="💬" title="Mensagem" subtitle="Recebida" gradientFrom="#22c55e" gradientTo="#16a34a"
                    onClick={() => addNode('trigger', 'Mensagem Recebida', { trigger_type: 'message_received' })} />
                  <ComponentBtn icon="⏰" title="Agendamento" subtitle="Timer" gradientFrom="#22c55e" gradientTo="#16a34a"
                    onClick={() => addNode('trigger', 'Agendamento', { trigger_type: 'schedule' })} />
                </div>
              </div>

              {/* Actions */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🎯 Ações
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <ComponentBtn icon="📱" title="WhatsApp" subtitle="Mensagem" gradientFrom="#3b82f6" gradientTo="#2563eb"
                    onClick={() => addNode('action', 'Enviar WhatsApp', { action_type: 'send_whatsapp' })} />
                  <ComponentBtn icon="📧" title="Email" subtitle="Enviar" gradientFrom="#3b82f6" gradientTo="#2563eb"
                    onClick={() => addNode('action', 'Enviar Email', { action_type: 'send_email' })} />
                  <ComponentBtn icon="✅" title="Tarefa" subtitle="Criar" gradientFrom="#3b82f6" gradientTo="#2563eb"
                    onClick={() => addNode('action', 'Criar Tarefa', { action_type: 'create_task' })} />
                  <ComponentBtn icon="👤" title="Atribuir" subtitle="Owner" gradientFrom="#3b82f6" gradientTo="#2563eb"
                    onClick={() => addNode('action', 'Atribuir Owner', { action_type: 'assign_owner' })} />
                  <ComponentBtn icon="🔗" title="Webhook" subtitle="HTTP" gradientFrom="#3b82f6" gradientTo="#2563eb"
                    onClick={() => addNode('action', 'Chamar Webhook', { action_type: 'call_webhook' })} />
                </div>
              </div>

              {/* Conditions */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f97316', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🔀 Condições
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <ComponentBtn icon="⚖️" title="IF Igual" subtitle="Comparar" gradientFrom="#f97316" gradientTo="#ea580c"
                    onClick={() => addNode('condition', 'IF (Igual)', { operator: '==' })} />
                  <ComponentBtn icon="📈" title="IF Maior" subtitle="Numérico" gradientFrom="#f97316" gradientTo="#ea580c"
                    onClick={() => addNode('condition', 'IF (Maior que)', { operator: '>' })} />
                  <ComponentBtn icon="📝" title="IF Contém" subtitle="Texto" gradientFrom="#f97316" gradientTo="#ea580c"
                    onClick={() => addNode('condition', 'IF (Contém)', { operator: 'contains' })} />
                </div>
              </div>

              {/* Loops */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#a855f7', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🔄 Loops
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <ComponentBtn icon="🔁" title="Loop" subtitle="Iterar" gradientFrom="#a855f7" gradientTo="#9333ea"
                    onClick={() => addNode('loop', 'Loop Iterator', {})} />
                  <ComponentBtn icon="⏱️" title="Delay" subtitle="Aguardar" gradientFrom="#a855f7" gradientTo="#9333ea"
                    onClick={() => addNode('loop', 'Delay', { delay_seconds: 60 })} />
                </div>
              </div>
            </div>

            {/* Help */}
            <div style={{
              padding: 14,
              borderTop: '1px solid rgba(0,0,0,0.05)',
              background: 'rgba(99, 102, 241, 0.05)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', marginBottom: 4 }}>💡 Dica</div>
              <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
                Clique em um componente para adicionar ao canvas. Arraste os nós para conectá-los.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Node Configuration Modal */}
      {showNodeModal && selectedNode && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 20,
            padding: 28,
            width: 400,
            maxWidth: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: 0 }}>⚙️ Configurar Nó</h2>
              <button
                onClick={() => setShowNodeModal(false)}
                style={{ border: 'none', background: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', padding: 0 }}
              >×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Tipo
                </label>
                <div style={{
                  padding: 14,
                  background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  textTransform: 'capitalize'
                }}>
                  {selectedNode.data.type === 'trigger' ? '⚡ Gatilho' :
                   selectedNode.data.type === 'action' ? '🎯 Ação' :
                   selectedNode.data.type === 'condition' ? '🔀 Condição' : '🔄 Loop'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nome
                </label>
                <input
                  type="text"
                  value={selectedNode.data.label}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    setNodes((nds) => nds.map((node) =>
                      node.id === selectedNode.id ? { ...node, data: { ...node.data, label: newLabel } } : node
                    ));
                    setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, label: newLabel } });
                  }}
                  style={{
                    width: '100%',
                    padding: 14,
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    fontSize: 14,
                    boxSizing: 'border-box',
                    background: '#fff'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Configuração (JSON)
                </label>
                <textarea
                  value={JSON.stringify(selectedNode.data.config || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value);
                      setNodes((nds) => nds.map((node) =>
                        node.id === selectedNode.id ? { ...node, data: { ...node.data, config } } : node
                      ));
                      setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, config } });
                    } catch { /* ignore */ }
                  }}
                  style={{
                    width: '100%',
                    padding: 14,
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                    fontFamily: 'monospace',
                    height: 100,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: '#fff'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  onClick={() => {
                    if (window.confirm('Remover este nó?')) {
                      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
                      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
                      setShowNodeModal(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                  }}
                >🗑️ Remover</button>
                <button
                  onClick={() => setShowNodeModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)'
                  }}
                >✓ Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
